import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { plants } from "@/lib/api";
import { saveCachedPlants, getCachedPlants } from "@/lib/offlineCache";

/**
 * Loads the Gallery scan list once we know who the user is, handles the
 * offline fallback (idb cache), and re-runs on `online` events.
 *
 * Behavior-preserving extraction from Gallery.jsx (iter56).
 *
 * @param {string|undefined} userId - user_id (may be undefined during hydration)
 * @param {import("i18next").TFunction} t - translation function
 * @returns {{
 *   items: Array<any>|null,
 *   setItems: React.Dispatch<React.SetStateAction<Array<any>|null>>,
 *   offline: boolean,
 *   load: () => Promise<void>,
 * }}
 */
export default function useGalleryOfflineLoader(userId, t) {
  const [items, setItems] = useState(null);
  const [offline, setOffline] = useState(false);

  // Snapshot `t` in a ref so a language change doesn't retrigger the effects.
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  const load = useCallback(async () => {
    try {
      const { data } = await plants.list();
      setItems(data.items || []);
      setOffline(false);
      saveCachedPlants(userId, data.items || []);
    } catch (err) {
      // Only treat genuine network errors as "offline" (axios sets err.code === 'ERR_NETWORK' or no response).
      const isNetworkErr = !err?.response || err?.code === "ERR_NETWORK";
      const cached = await getCachedPlants(userId);
      if (cached?.items) {
        setItems(cached.items);
        setOffline(isNetworkErr);
        if (isNetworkErr) toast.message(tRef.current("gallery.offlineNotice"));
      } else {
        setItems([]);
        setOffline(isNetworkErr);
        if (!isNetworkErr) toast.error(tRef.current("gallery.toastLoadFail"));
      }
    }
  }, [userId]);

  // Only run once we actually know who the user is (avoids the pre-auth 401 + 2nd load on user resolve).
  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  useEffect(() => {
    const onOnline = () => { load(); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [load]);

  return { items, setItems, offline, load };
}
